'use client';

import { useState, useEffect, useRef, forwardRef, useImperativeHandle } from 'react';
import {
  Box,
  Stack,
  TextField,
  Button,
  CircularProgress,
  FormControl,
  MenuItem,
  Select,
  InputLabel,
  IconButton,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableFooter,
  Typography,
  Paper
} from '@mui/material';
import { Formik } from 'formik';
import * as Yup from 'yup';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import PickerField from '../../../../components/PickerField';
import usePickerPagination from '../../../../hooks/usePickerPagination';
import { useCompanyDataList } from '../../../../stores/companyStore';
import { useWorkshopList } from '../../../../stores/workshopStore';
import { createMaintenance, updateMaintenance, getMaintenanceById } from '../../../../stores/vehicleMaintenanceStore';
import { useMasterVehicleList, updateVehicle } from '../../../../stores/masterVehicleStore';
import {
  useServiceTypeList,
  deleteVehicleServiceType,
  updateVhicleServiceType,
  createVehicleServiceType
} from '../../../../stores/serviceTypeStore';
import DeleteConfirmDialog from '../../../../components/DeleteConfirmDialog';
import { useLanguage } from '../../../../utils/i18n';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';
import { isZeroDate } from '../../../../utils/dateTimeUtils';
import FormDialog from '../../../../components/FormDialog';

const toIsoDate = (d) => {
  if (!d) return null;
  const dj = dayjs(d);
  if (!dj.isValid() || isZeroDate(dj)) return null;
  return `${dj.format('YYYY-MM-DD')}T00:00:00Z`;
};

const parsePickerValue = (val) => {
  if (!val) return null;
  if (dayjs.isDayjs(val)) return val;
  const dj = dayjs(val);
  return dj.isValid() && !isZeroDate(val) ? dj : null;
};

const addDaysStr = (baseStr, days) => {
  const dj = dayjs(baseStr);
  if (!dj.isValid()) return '';
  return dj.add(Number(days || 0), 'day').format('YYYY-MM-DD');
};

const WorkOrderAddEdit = forwardRef(
  (
    { open = false, id = null, onClose = null, onSuccess = null, onError = null, onSubmitStart = () => {}, onSubmitEnd = () => {} },
    ref
  ) => {
    const [initialValues, setInitialValues] = useState({
      status: '',
      company: '',
      invoice_number: '',
      vm_date: id ? null : dayjs(),
      license_plate: '',
      workshop: '',
      date_completed: null,
      date_completed_actual: null,
      current_odometer: '',
      actual_odometer: ''
    });
    const { t } = useLanguage();
    const [loading, setLoading] = useState(Boolean(id));
    const [tasks, setTasks] = useState([]);
    const formikRef = useRef();

    const [confirmOpen, setConfirmOpen] = useState(false);
    const [confirmLoading] = useState(false);
    const [confirmTask, setConfirmTask] = useState(null);
    const handleDeleteClick = (row) => {
      setConfirmTask(row);
      setConfirmOpen(true);
    };

    const handleConfirmDelete = () => {
      setTasks((prev) =>
        prev
          .map((t) => {
            if (t.tempId !== confirmTask.tempId) return t;
            if (t.__action === 'create') return null;
            return { ...t, __action: 'delete' };
          })
          .filter(Boolean)
      );

      setConfirmTask(null);
      setConfirmOpen(false);
    };
    const [selectedCompanyId, setSelectedCompanyId] = useState('');

    const companyPickerParams = usePickerPagination({
      fetchHook: useCompanyDataList,
      buildArgs: (state) => [state.page, state.pageSize, state.searchKey, state.searchValue],
      searchKey: 'name',
      searchPlaceholder: t('general.search', 'Search...')
    });

    const vehiclePickerParams = usePickerPagination({
      fetchHook: useMasterVehicleList,
      buildArgs: (state) => [
        state.page,
        state.pageSize,
        selectedCompanyId ? [{ value: selectedCompanyId }] : [],
        state.searchKey === 'name' ? 'license_plate' : state.searchKey,
        state.searchValue
      ],
      searchKey: 'license_plate',
      company: selectedCompanyId
    });

    const workshopPickerParams = usePickerPagination({
      fetchHook: useWorkshopList,
      buildArgs: (state) => [state.page, state.pageSize, state.searchKey, state.searchValue, selectedCompanyId || ''],
      searchKey: 'name',
      company: selectedCompanyId
    });

    const serviceTypePickerParams = usePickerPagination({
      fetchHook: useServiceTypeList,
      buildArgs: (state) => [state.page, state.pageSize, state.searchKey, state.searchValue, selectedCompanyId || ''],
      searchKey: 'name',
      company: selectedCompanyId
    });

    const [editingCost, setEditingCost] = useState({});
    const formatCurrency = (v) =>
      new Intl.NumberFormat('id-ID', { style: 'currency', currency: 'IDR', maximumFractionDigits: 0 }).format(Number(v || 0));

    const parseNumberFromInput = (str) => {
      if (str == null) return 0;
      const digits = String(str).replace(/[^\d]/g, '');
      return digits === '' ? 0 : Number(digits);
    };
    useEffect(() => {
      setSelectedCompanyId(initialValues.company || '');
    }, [initialValues.company]);

    useImperativeHandle(ref, () => ({ submitForm: () => formikRef.current?.submitForm?.() }));

    useEffect(() => {
      let mounted = true;
      const fetch = async () => {
        if (!open) return;

        if (!id) {
          setLoading(false);
          const storedCompany = localStorage.getItem('companyId');
          const firstStored = storedCompany ? String(storedCompany).split(',')[0].trim() : '';
          const validCompany = firstStored && firstStored !== 'all' ? firstStored : '';
          setInitialValues({
            status: '',
            company: validCompany,
            invoice_number: '',
            vm_date: dayjs(),
            license_plate: '',
            workshop: '',
            date_completed: null,
            date_completed_actual: null,
            current_odometer: '',
            actual_odometer: ''
          });
          setTasks([]);
          return;
        }

        setLoading(true);
        try {
          const data = await getMaintenanceById(id);
          if (!mounted) return;
          if (data) {
            setInitialValues({
              status: data.status || '',
              company: String(data.company || ''),
              invoice_number: data.invoice_number || '',
              vm_date: data.vm_date && !isZeroDate(data.vm_date) ? dayjs(data.vm_date) : dayjs(),
              license_plate: String(data.vehicle || ''),
              workshop: String(data.workshop || ''),
              date_completed: data.date_completed && !isZeroDate(data.date_completed) ? dayjs(data.date_completed) : null,
              date_completed_actual:
                data.date_completed_actual && !isZeroDate(data.date_completed_actual) ? dayjs(data.date_completed_actual) : null,
              current_odometer: data.current_odometer || '',
              actual_odometer: data.actual_odometer || ''
            });
            setTasks(
              (data.vm_service_type || []).map((s) => ({
                tempId: s.id,
                id: s.id,
                service_type: String(s.service_type || ''),
                service_type_name: s.service_type_name || '',
                next_service_odo: s.next_service_odo || '',
                next_service_date: s.next_service_date?.split('T')[0] || '',
                remarks: s.remarks || '',
                service_cost: s.price || 0,
                isEditing: false,
                __action: null
              }))
            );
          }
        } catch (err) {
          console.error(err);
          onError?.(t('workOrder.failedLoadData', 'Failed to load data'));
        } finally {
          if (mounted) setLoading(false);
        }
      };
      fetch();
      return () => {
        mounted = false;
      };
    }, [id, open, onError, t]);

    const validationSchema = Yup.object().shape({
      company: Yup.string().required(t('workOrder.validationCompany', 'Company is required')),

      license_plate: Yup.string().required(t('workOrder.validationVehicle', 'Vehicle is required')),

      workshop: Yup.string().required(t('workOrder.validationWorkshop', 'Workshop is required')),

      status: Yup.string().required(t('workOrder.validationStatus', 'Status is required'))
    });

    const recalcTaskForService = (task, curOdo, vmDate) => {
      const intervalDistance = Number(task.interval_distance || 0);
      const intervalTime = Number(task.interval_time || 0);
      const baseOdo = Number(curOdo || 0);
      const nextOdo = intervalDistance ? baseOdo + intervalDistance : task.next_service_odo || '';
      const nextDate = intervalTime ? addDaysStr(vmDate || '', intervalTime) : task.next_service_date || '';
      return { ...task, next_service_odo: nextOdo || '', next_service_date: nextDate || '' };
    };

    const handleVehicleChange = (val, setFieldValue, rawVehicle = null) => {
      const odo = (rawVehicle?.current_odo || rawVehicle?.current_odometer || 0) / 1000;
      setFieldValue('license_plate', val);
      setFieldValue('current_odometer', odo);
      setFieldValue('actual_odometer', odo);
    };
    const handleActualOdoChange = (val, setFieldValue) => {
      setFieldValue('actual_odometer', val);
      setFieldValue('current_odometer', val); // Auto sync
    };

    const handleVmDateChange = (val, setFieldValue) => {
      setFieldValue('vm_date', val);
      const cur = formikRef.current?.values?.current_odometer ?? 0;
      setTasks((prev) => prev.map((r) => (r.service_type ? recalcTaskForService(r, cur, val) : r)));
    };

    const handleAddTask = () => {
      setTasks((t) => [
        ...t,
        {
          tempId: Date.now(),
          service_type_name: '',
          service_cost: 0,
          remarks: '',
          __action: 'create',
          isEditing: true
        }
      ]);
    };
    const handleEditTask = (tempId) => {
      setTasks((t) => t.map((r) => (r.tempId === tempId ? { ...r, isEditing: true, backup: { ...r } } : r)));
    };
    const handleCancelTask = (tempId) => {
      setTasks((t) =>
        t
          .map((r) => {
            if (r.tempId !== tempId) return r;
            if (r.__action === 'create') return null;
            return { ...r.backup, isEditing: false, backup: undefined };
          })
          .filter(Boolean)
      );
    };

    const handleConfirmTask = (task) => {
      setTasks((t) =>
        t.map((r) =>
          r.tempId === task.tempId
            ? {
                ...r,
                isEditing: false,
                __action: r.id ? 'update' : 'create',
                backup: undefined
              }
            : r
        )
      );
    };

    const handleServiceSelect = (tempId, val, rawSvc = null) => {
      const formVals = formikRef.current?.values ?? {};

      let mappedSvc = null;
      if (rawSvc) {
        mappedSvc = {
          value: String(rawSvc.id ?? rawSvc.value ?? rawSvc),
          label: rawSvc.service_type_name ?? rawSvc.name ?? String(rawSvc.id ?? rawSvc.value ?? rawSvc),
          schedule_by: rawSvc.schedule_by ?? rawSvc.service_length_unit ?? '',
          service_cost: rawSvc.service_cost ?? rawSvc.price ?? 0,
          interval_distance: rawSvc.interval_distance ?? rawSvc.service_km ?? 0,
          interval_time: rawSvc.interval_time ?? rawSvc.service_length ?? 0
        };
      }

      setTasks((t) =>
        t.map((r) =>
          r.tempId === tempId
            ? recalcTaskForService(
                {
                  ...r,
                  service_type: mappedSvc ? String(mappedSvc.value) : '',
                  service_type_name: mappedSvc ? mappedSvc.label : '',
                  schedule_by: mappedSvc?.schedule_by ?? '',
                  service_cost: mappedSvc?.service_cost ?? r.service_cost,
                  interval_distance: mappedSvc?.interval_distance ?? 0,
                  interval_time: mappedSvc?.interval_time ?? 0,
                  isEditing: true,
                  __action: r.id ? 'update' : 'create'
                },
                formVals.current_odometer,
                formVals.vm_date
              )
            : r
        )
      );
    };

    const handleTaskChange = (tempId, key, value) => {
      setTasks((t) =>
        t.map((r) =>
          r.tempId === tempId
            ? {
                ...r,
                [key]: value,
                __action: r.id ? 'update' : 'create'
              }
            : r
        )
      );
    };
    useEffect(() => {
      if (id) return;
      const stored = typeof window !== 'undefined' ? localStorage.getItem('companyId') : null;
      if (!stored) return;

      const firstId = stored.split(',')[0]?.trim();
      if (firstId) {
        setInitialValues((prev) => {
          if (prev.company) return prev;
          return {
            ...prev,
            company: Number(firstId) || firstId
          };
        });
      }
    }, [id]);

    const totalCost = tasks.filter((t) => t.__action !== 'delete').reduce((a, b) => a + (Number(b.service_cost) || 0), 0);

    const updatePriceOnly = async (workOrderId, newPrice) => {
      if (!workOrderId) return;
      const minimalPayload = {
        id: Number(workOrderId),
        price: Number(newPrice || 0)
      };

      try {
        await updateMaintenance(workOrderId, minimalPayload);
      } catch (err) {
        console.error(`Failed to update Work Order price ${workOrderId}:`, err);
      }
    };

    const handleSubmit = async (values, { setSubmitting, validateForm, setTouched }) => {
      setSubmitting(true);
      onSubmitStart();

      try {
        const errors = await validateForm();
        if (Object.keys(errors).length > 0) {
          setTouched({
            company: true,
            license_plate: true,
            workshop: true,
            status: true
          });

          setSubmitting(false);
          onSubmitEnd();
          return;
        }

        const companyId = values.company ? Number(values.company) : null;
        if (!companyId) {
          throw new Error(t('workOrder.companyRequired', 'Company must be selected'));
        }

        const activeTasks = tasks.filter((t) => t.__action !== 'delete');
        if (activeTasks.length === 0 || !activeTasks.every((t) => Boolean(t.service_type || t.service_type_name))) {
          onError?.(t('workOrder.taskRequired', 'Please add at least one valid task'));
          setSubmitting(false);
          onSubmitEnd();
          return;
        }

        if (values.date_completed && !dayjs(values.date_completed).isValid()) {
          onError?.(t('workOrder.invalidEstimatedDate', 'Estimated Completion Date is invalid'));
          setSubmitting(false);
          onSubmitEnd();
          return;
        }

        if (values.date_completed_actual && !dayjs(values.date_completed_actual).isValid()) {
          onError?.(t('workOrder.invalidActualDate', 'Actual Completion Date is invalid'));
          setSubmitting(false);
          onSubmitEnd();
          return;
        }

        const tasksToCreate = tasks.filter((t) => t.__action === 'create' || t.id === null);
        const tasksToUpdate = tasks.filter((t) => t.__action === 'update' && t.id);
        const tasksToDelete = tasks.filter((t) => t.__action === 'delete' && t.id);

        let finalTasks = tasks.filter((t) => t.__action !== 'delete');
        if (id) {
          await Promise.all(
            tasksToDelete.map((t) =>
              deleteVehicleServiceType(t.id).catch((err) => {
                console.warn(`Failed to delete Service Type ID ${t.id}:`, err);
              })
            )
          );

          await Promise.all(
            tasksToUpdate.map((task) => {
              const taskPayload = {
                status: values.status,
                vehicle: values.license_plate ? Number(values.license_plate) : null,
                current_odometer: values.current_odometer ? Number(values.current_odometer) : null,
                actual_odometer: values.actual_odometer ? Number(values.actual_odometer) : null,
                workshop: values.workshop ? Number(values.workshop) : null,
                invoice_number: values.invoice_number || '',
                date_completed: values.date_completed ? toIsoDate(values.date_completed) : null,
                date_completed_actual: values.date_completed_actual ? toIsoDate(values.date_completed_actual) : null,
                service_type: task.service_type ? Number(task.service_type) : null,
                price: Number(task.service_cost || 0),
                remarks: task.remarks || '',
                next_service_odo: task.next_service_odo ? Number(task.next_service_odo) : null,
                next_service_date: task.next_service_date ? toIsoDate(task.next_service_date) : null,
                interval_distance: Number(task.interval_distance || 0),
                interval_time: Number(task.interval_time || 0)
              };
              return updateVhicleServiceType(task.id, taskPayload).catch((err) => {
                console.error(`Failed to update Task ID ${task.id}:`, err);
                const backendMsg = err?.response?.data?.message || err?.message;
                throw new Error(
                  `${t('workOrder.failedUpdateTask', 'Failed to update task')}: ${task.service_type_name || ''}${backendMsg ? ` - ${backendMsg}` : ''}`
                );
              });
            })
          );
          const createdResults = await Promise.all(
            tasksToCreate.map((task) => {
              const taskPayload = {
                action: '',
                company: Number(values.company),
                current_odometer: Number(values.current_odometer || values.actual_odometer || 0),
                id: 0,
                interval_time: Number(task.interval_time || 0),
                next_odo: Number(task.next_service_odo || 0),
                next_service_date: task.next_service_date || null,
                next_service_odo: Number(task.next_service_odo || 0),
                price: Number(task.service_cost || 0),
                remarks: task.remarks || '',
                schedule_by: task.schedule_by || '',
                service_type: task.service_type ? Number(task.service_type) : null,
                service_type_name: task.service_type_name || '',
                vehicle_maintenance: Number(id)
              };
              return createVehicleServiceType(taskPayload).catch((err) => {
                console.error(`Failed to create new Task:`, err);
                const backendMsg = err?.response?.data?.message || err?.message;
                throw new Error(
                  `${t('workOrder.failedCreateTask', 'Failed to create new Task')}: ${task.service_type_name || ''}${backendMsg ? ` - ${backendMsg}` : ''}`
                );
              });
            })
          );

          const newTasksWithIds = createdResults
            .filter((res) => res && res.data && res.status)
            .map((res) => ({
              id: res.data.id,
              tempId: tasksToCreate.find((t) => t.service_type === String(res.data.service_type) && t.price === res.data.price)?.tempId
            }));

          finalTasks = finalTasks
            .map((t) => {
              const newIdInfo = newTasksWithIds.find((ni) => ni.tempId === t.tempId);
              if (newIdInfo) {
                return { ...t, id: newIdInfo.id };
              }
              return t;
            })
            .filter((t) => t.__action !== 'delete');
        }

        const activeTasksForPayload = tasks.filter((t) => t.__action !== 'delete');
        const finalTotalCost = activeTasksForPayload.reduce((sum, t) => sum + Number(t.service_cost || 0), 0);

        if (id) {
          await updatePriceOnly(id, finalTotalCost);
        }

        const payload = {
          //id: id ?? 0,
          vehicle: values.license_plate ? Number(values.license_plate) : 0,
          company: companyId,
          workshop: values.workshop ? Number(values.workshop) : 0,
          invoice_number: values.invoice_number || '',
          status: values.status,
          //license_plate: findOption(vehicleOptions, values.license_plate)?.label ?? values.license_plate ?? "",
          date_completed: values.date_completed ? toIsoDate(values.date_completed) : toIsoDate(dayjs()),
          vm_date: values.vm_date ? toIsoDate(values.vm_date) : toIsoDate(dayjs()),
          date_completed_actual: values.date_completed_actual ? toIsoDate(values.date_completed_actual) : toIsoDate(dayjs()),
          current_odometer: values.current_odometer ? Number(values.current_odometer) : 0,
          actual_odometer: values.actual_odometer ? Number(values.actual_odometer) : 0,
          price: Number(finalTotalCost)
        };
        if (id) {
          const updatePayload = {
            ...payload,
            vehicle_task: activeTasksForPayload.map((s) => ({
              id: s.id ?? 0,
              service_type: s.service_type ? Number(s.service_type) : 0,
              price: Number(s.service_cost || 0),
              remarks: s.remarks || '',
              next_service_odo: s.next_service_odo ? Number(s.next_service_odo) : 0,
              next_service_date: s.next_service_date ? toIsoDate(s.next_service_date) : '',
              interval_distance: Number(s.interval_distance || 0),
              interval_time: Number(s.interval_time || 0)
            })),
            vehicle_maintenance: id
          };
          await updateMaintenance(id, updatePayload);
          onSuccess && onSuccess(t('workOrder.updatedSuccess', 'Updated successfully'), payload.status);
        } else {
          const createPayload = {
            ...payload,
            vm_service_type: activeTasksForPayload.map((s) => ({
              id: s.id ?? 0,
              service_type: s.service_type ? Number(s.service_type) : 0,
              price: Number(s.service_cost || 0),
              remarks: s.remarks || '',
              next_service_odo: s.next_service_odo ? Number(s.next_service_odo) : 0,
              next_service_date: s.next_service_date ? toIsoDate(s.next_service_date) : '',
              interval_distance: Number(s.interval_distance || 0),
              interval_time: Number(s.interval_time || 0)
            })),
            is_maintenance: true
          };
          await createMaintenance(createPayload);
          onSuccess && onSuccess(t('workOrder.createdSuccess', 'Created successfully'), payload.status);
        }
        if (values.license_plate && values.actual_odometer) {
          await updateVehicle(values.license_plate, { current_odo: Number(values.actual_odometer) });
        }
        const finalTasksCleaned = finalTasks.map((t) => {
          return { ...t, __action: null, isEditing: false };
        });

        setTasks(finalTasksCleaned);

        onClose && onClose();
      } catch (err) {
        console.error('Error during handleSubmit:', err);
        onError && onError(err?.response?.data?.message || err?.message || t('workOrder.submitFailed', 'Submit failed'));
      } finally {
        onSubmitEnd();
        setSubmitting(false);
      }
    };
    const fmtDateID = (iso) => {
      if (isZeroDate(iso)) return '';
      return dayjs(iso).format('DD MMM YYYY');
    };

    return (
      <>
        <Formik
          key={id ? `edit-${id}` : `add-${open}`}
          innerRef={formikRef}
          initialValues={initialValues}
          enableReinitialize
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
        >
          {(formik) => {
            const { values, handleChange, setFieldValue, handleSubmit, isSubmitting } = formik;
            const status = values.status;
            const estEnabled = status === 'onprogress' || status === '';
            const actualEnabled = status === 'completed' || status === '';
            const activeTasks = tasks.filter((t) => t.__action !== 'delete');
            const hasTasks = activeTasks.length > 0 && activeTasks.every((t) => Boolean(t.service_type || t.service_type_name));
            return (
              <FormDialog
                open={open}
                onClose={onClose}
                title={id ? t('workOrder.editWorkOrder', 'Edit Work Order') : t('workOrder.addWorkOrder', 'Add Work Order')}
                fullScreenMode={true}
                fullHeight={false}
                actions={
                  <>
                    <Button
                      variant="contained"
                      type="submit"
                      form="work-order-form"
                      disabled={isSubmitting || loading || !hasTasks}
                      sx={{
                        backgroundColor: '#0072DA',
                        textTransform: 'none',
                        borderRadius: 1,
                        px: 3,
                        fontWeight: 700,
                        boxShadow: '0 8px 20px rgba(25,118,210,0.12)',
                        '&.Mui-disabled': {
                          backgroundColor: 'rgba(0, 0, 0, 0.12)',
                          color: 'rgba(0, 0, 0, 0.26)'
                        }
                      }}
                      startIcon={isSubmitting ? <CircularProgress size={18} color="inherit" /> : null}
                    >
                      {isSubmitting ? t('buttonTitle.processing', 'Processing...') : t('buttonTitle.save', 'Save')}
                    </Button>
                    <Button onClick={onClose} disableRipple disableElevation variant="outlined">
                      {t('buttonTitle.cancel', 'Cancel')}
                    </Button>
                  </>
                }
              >
                {loading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', p: 4, minHeight: 200, alignItems: 'center' }}>
                    <CircularProgress />
                  </Box>
                ) : (
                  <Box sx={{ width: '100%', px: { xs: 2, md: 3 }, py: { xs: 2, md: 3 } }}>
                    <form id="work-order-form" onSubmit={handleSubmit}>
                      <Stack spacing={2}>
                        <Paper sx={{ width: '100%', maxWidth: 'none', px: { xs: 2, md: 3 }, py: { xs: 2, md: 3 } }}>
                          <Box sx={{ display: 'flex', gap: 4, alignItems: 'stretch', width: '100%' }}>
                            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
                              <FormControl fullWidth error={formik.touched.status && Boolean(formik.errors.status)}>
                                <InputLabel>{t('workOrder.status', 'Status')}</InputLabel>

                                <Select
                                  label={t('workOrder.status', 'Status')}
                                  name="status"
                                  value={values.status || ''}
                                  onChange={(e) => setFieldValue('status', e.target.value)}
                                  onBlur={formik.handleBlur}
                                >
                                  <MenuItem value="onprogress">{t('workOrder.onProgress', 'On Progress')}</MenuItem>
                                  <MenuItem value="completed">{t('workOrder.completed', 'Completed')}</MenuItem>
                                </Select>

                                {formik.touched.status && formik.errors.status && (
                                  <Typography variant="caption" color="error">
                                    {formik.errors.status}
                                  </Typography>
                                )}
                              </FormControl>
                              <PickerField
                                label={t('general.company', 'Company')}
                                {...companyPickerParams}
                                value={values.company || ''}
                                onChange={(val) => {
                                  const valStr = val ? String(val) : '';
                                  setFieldValue('company', valStr);
                                  setSelectedCompanyId(valStr);
                                  setFieldValue('license_plate', '');
                                  setFieldValue('workshop', '');
                                }}
                                error={formik.touched.company && Boolean(formik.errors.company)}
                                helperText={formik.touched.company && formik.errors.company}
                              />
                              <TextField
                                label={t('workOrder.invoiceNumber', 'Invoice Number')}
                                name="invoice_number"
                                value={values.invoice_number || ''}
                                onChange={handleChange}
                                fullWidth
                              />
                            </Box>
                            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
                              <DatePicker
                                label={t('workOrder.woDate', 'WO Date')}
                                format="DD-MM-YYYY"
                                value={parsePickerValue(values.vm_date)}
                                onChange={(newValue) => handleVmDateChange(newValue, setFieldValue)}
                                disabled
                                slotProps={{ textField: { size: 'small', fullWidth: true } }}
                              />
                              <PickerField
                                label={t('workOrder.vehicle', 'Vehicle')}
                                {...vehiclePickerParams}
                                value={values.license_plate || ''}
                                onChange={(val) => {
                                  if (val) {
                                    const opt = vehiclePickerParams.options.find((o) => String(o.value) === String(val));
                                    handleVehicleChange(val, setFieldValue, opt?.raw);
                                  } else {
                                    setFieldValue('license_plate', '');
                                  }
                                }}
                                error={formik.touched.license_plate && Boolean(formik.errors.license_plate)}
                                helperText={formik.touched.license_plate && formik.errors.license_plate}
                              />
                              <PickerField
                                label={t('workOrder.workshop', 'Workshop')}
                                {...workshopPickerParams}
                                value={values.workshop || ''}
                                onChange={(val) => setFieldValue('workshop', val ? String(val) : '')}
                                error={formik.touched.workshop && Boolean(formik.errors.workshop)}
                                helperText={formik.touched.workshop && formik.errors.workshop}
                              />
                            </Box>
                            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
                              <DatePicker
                                label={t('workOrder.estimatedCompletionDate', 'Estimated Completion Date')}
                                format="DD-MM-YYYY"
                                value={parsePickerValue(values.date_completed)}
                                onChange={(newValue) => setFieldValue('date_completed', newValue)}
                                disabled={!estEnabled}
                                slotProps={{ textField: { size: 'small', fullWidth: true } }}
                              />
                              <TextField
                                label={t('workOrder.currentOdo', 'Current Odo')}
                                type="number"
                                name="current_odometer"
                                value={values.current_odometer ?? ''}
                                onChange={handleChange}
                                fullWidth
                                disabled
                              />
                            </Box>
                            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
                              <DatePicker
                                label={t('workOrder.actualCompletionDate', 'Actual Completion Date')}
                                format="DD-MM-YYYY"
                                value={parsePickerValue(values.date_completed_actual)}
                                onChange={(newValue) => setFieldValue('date_completed_actual', newValue)}
                                disabled={!actualEnabled}
                                slotProps={{ textField: { size: 'small', fullWidth: true } }}
                              />
                              <TextField
                                label={t('workOrder.actualOdo', 'Actual Odo')}
                                type="number"
                                name="actual_odometer"
                                value={values.actual_odometer ?? ''}
                                onChange={(e) => handleActualOdoChange(e.target.value, setFieldValue)}
                                fullWidth
                              />
                            </Box>
                          </Box>
                        </Paper>
                        <Box>
                          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
                            <Typography variant="h5" sx={{ fontWeight: 600 }}>
                              {t('workOrder.listTask', 'List Task')}
                            </Typography>
                            <Button startIcon={<AddIcon />} variant="outlined" onClick={handleAddTask}>
                              {t('workOrder.add', 'Add')}
                            </Button>
                          </Stack>
                          <Paper>
                            <Table size="small">
                              <TableHead>
                                <TableRow>
                                  <TableCell>{t('workOrder.task', 'Task')}</TableCell>
                                  <TableCell>{t('workOrder.nextServiceOdo', 'Next Service Odo')}</TableCell>
                                  <TableCell>{t('workOrder.nextServiceDate', 'Next Service Date')}</TableCell>
                                  <TableCell>{t('workOrder.remark', 'Remark')}</TableCell>
                                  <TableCell align="right">{t('workOrder.serviceCost', 'Service Cost')}</TableCell>
                                  <TableCell></TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {activeTasks.length === 0 ? (
                                  <TableRow>
                                    <TableCell colSpan={6} align="center" sx={{ py: 3, color: 'text.secondary' }}>
                                      {t('workOrder.noTasksAdded', 'No tasks added yet. Click "+ Add" above to add a task.')}
                                    </TableCell>
                                  </TableRow>
                                ) : (
                                  activeTasks.map((r) => (
                                    <TableRow key={r.tempId}>
                                      <TableCell sx={{ minWidth: 240 }}>
                                        <PickerField
                                          label=""
                                          {...serviceTypePickerParams}
                                          value={r.service_type || ''}
                                          getOptionDisabled={(opt) => {
                                            const optId = String(opt.id || opt.value || opt);
                                            const isSelected = tasks
                                              .filter((t) => t.__action !== 'delete')
                                              .some((t) => String(t.service_type) === optId);
                                            return isSelected && String(r.service_type) !== optId
                                              ? t('workOrder.alreadySelected', 'Already selected')
                                              : false;
                                          }}
                                          onChange={(val) => {
                                            const opt = serviceTypePickerParams.options.find((o) => String(o.value) === String(val));
                                            handleServiceSelect(r.tempId, val, opt?.raw);
                                          }}
                                        />
                                      </TableCell>
                                      <TableCell>{r.next_service_odo || '-'}</TableCell>
                                      <TableCell>{fmtDateID(r.next_service_date)}</TableCell>
                                      <TableCell>
                                        <TextField
                                          size="small"
                                          value={r.remarks || ''}
                                          disabled={!r.isEditing}
                                          onChange={(e) => handleTaskChange(r.tempId, 'remarks', e.target.value)}
                                        />
                                      </TableCell>

                                      <TableCell align="right">
                                        <TextField
                                          size="small"
                                          disabled={!r.isEditing}
                                          value={
                                            typeof editingCost[r.tempId] !== 'undefined'
                                              ? editingCost[r.tempId]
                                              : r.service_cost != null
                                                ? formatCurrency(r.service_cost)
                                                : ''
                                          }
                                          onFocus={() => {
                                            setEditingCost((s) => ({ ...s, [r.tempId]: String(Number(r.service_cost || 0)) }));
                                          }}
                                          onChange={(e) => {
                                            const raw = e.target.value;
                                            setEditingCost((s) => ({ ...s, [r.tempId]: raw }));
                                            const parsed = parseNumberFromInput(raw);
                                            setTasks((t) => t.map((it) => (it.tempId === r.tempId ? { ...it, service_cost: parsed } : it)));
                                          }}
                                          onBlur={() => {
                                            setEditingCost((s) => {
                                              const copy = { ...s };
                                              delete copy[r.tempId];
                                              return copy;
                                            });
                                          }}
                                          slotProps={{
                                            input: {
                                              style: { textAlign: 'right' }
                                            }
                                          }}
                                        />
                                      </TableCell>

                                      <TableCell align="right">
                                        {r.isEditing ? (
                                          <>
                                            <IconButton onClick={() => handleConfirmTask(r)}>
                                              <CheckIcon color="success" />
                                            </IconButton>
                                            <IconButton onClick={() => handleCancelTask(r.tempId)}>
                                              <CloseIcon color="error" />
                                            </IconButton>
                                          </>
                                        ) : (
                                          <>
                                            <IconButton onClick={() => handleEditTask(r.tempId)}>
                                              <EditIcon color="primary" />
                                            </IconButton>
                                            <IconButton onClick={() => handleDeleteClick(r)}>
                                              <DeleteIcon color="error" />
                                            </IconButton>
                                          </>
                                        )}
                                      </TableCell>
                                    </TableRow>
                                  ))
                                )}
                              </TableBody>
                              <TableFooter>
                                <TableRow>
                                  <TableCell colSpan={4} />
                                  <TableCell align="right" sx={{ fontWeight: 700 }}>
                                    {formatCurrency(totalCost)}
                                  </TableCell>
                                  <TableCell />
                                </TableRow>
                              </TableFooter>
                            </Table>
                          </Paper>
                        </Box>
                      </Stack>
                    </form>
                  </Box>
                )}
              </FormDialog>
            );
          }}
        </Formik>

        <DeleteConfirmDialog
          open={confirmOpen}
          loading={confirmLoading}
          onClose={() => !confirmLoading && setConfirmOpen(false)}
          onConfirm={handleConfirmDelete}
          title={t('deleteConfirm.title', 'Confirm Delete')}
          message={t('deleteConfirm.message', 'Are you sure you want to delete this item?')}
          confirmLabel={t('deleteConfirm.label', 'Delete')}
          cancelLabel={t('deleteConfirm.cancel', 'Cancel')}
        />
      </>
    );
  }
);

export default WorkOrderAddEdit;
